import { createRouter, createWebHistory } from "vue-router";

// Layouts
import HomeView from "@/layouts/HomeView.vue";
import HotTopicsView from "@/layouts/HotTopicsView.vue";
import MultiTopicView from "@/layouts/MultiTopicView.vue";
import PageNotFoundView from "@/layouts/PageNotFoundView.vue";
import SearchResults from "@/layouts/SearchResults.vue";

/*
  NOTE:
  When adding a new page, make sure to put it in the pages/ folder of the repository. 
  The page should have a *name*.js and a *name*.html file. Follow the same format used by other pages.
  This is necessary so the page gets cached by the service worker (via prebuild.js).

  Then:
  - Import and add it to the appropriate list below (hotTopics or multiTopics).
    Hot topics should follow the naming convention HT<PageName>.
  - Add the page to PageList.js so it can be searched.
  - Add the page to LeftNav if it should be accessible from the navigation menu.
  - Add the page to MobileTopNav if it should be accessible from the mobile top navigation menu.
  - Verify the page content or name is searchable via the search bar.
*/

// Hot 
import HTAutonomousVehicle from "@/pages/HT-AutonomousVehicle.js";
import HTPrivatePropertyCrashes from "@/pages/HT-PrivatePropertyCrashes.js";
import HTAtFault from "@/pages/HT-AtFault.js";
import HTContributingFactors from "@/pages/HT-ContributingFactors.js";
import HTCrashInvestigation from "@/pages/HT-CrashInvestigation.js";
import HTNarrative from "@/pages/HT-Narrative.js";
import HTCommercialMotorVehicles from "@/pages/HT-CommercialMotorVehicles.js";
import HTOccupantProtection from "@/pages/HT-OccupantProtection.js";
import HTFatalCrash from "@/pages/HT-FatalCrash.js";
import HTHitAndRun from "@/pages/HT-HitAndRun.js";
import HTLocation from "@/pages/HT-Location.js";

// Multi Topics
import CollisionDetails from "@/pages/collision_details_and_circumstances.js";
import commercial_motor_vehicles from "@/pages/commercial_motor_vehicles.js";
import contributing_factors from "@/pages/contributing_factors.js";
import crash_location from "@/pages/crash_location.js";
import crash_report_forms from "@/pages/crash_report_forms.js";
import crash_specific_information from "@/pages/crash_specific_information.js";
import diagrams from "@/pages/diagrams.js";
import executive_summary from "@/pages/executive_summary.js";
import location_identification_tips from "@/pages/location_identification_tips.js";
import narrative from "@/pages/Narrative.js";
import occupant_information from "@/pages/occupant_information.js";
import overview from "@/pages/overview.js";
import property_damage_witness_information from "@/pages/property_damage_witness_information.js";
import reporting_fatal_crashes from "@/pages/reporting_fatal_crashes.js";
import reporting_motor_vehicle_crashes_with_trains from "@/pages/reporting_motor_vehicle_crashes_with_trains.js";
import roadway_details from "@/pages/roadway_details.js";
import submitting_a_crash_report from "@/pages/submitting_a_crash_report.js";
import unit_operator_details from "@/pages/unit_operator_details.js";
import vehicle_details from "@/pages/vehicle_details.js";
import data from "@/pages/data.js";
import rules from "@/pages/rules.js";
import contactus from "@/pages/contactus.js";
import learning_module from "@/pages/learning_module.js";
import faq from "@/pages/faq.js";
import release_notes from "@/pages/release_notes.js";

// Lists and Tables
import county_list from "@/pages/county_list";
import glossary from "@/pages/glossary.js";
import codes from "@/pages/georgia_codes.js";

let hotTopics = [
  HTAutonomousVehicle,
  HTPrivatePropertyCrashes,
  HTAtFault,
  HTContributingFactors,
  HTCrashInvestigation,
  HTNarrative,
  HTCommercialMotorVehicles,
  HTOccupantProtection,
  HTFatalCrash,
  HTHitAndRun,
  HTLocation,
];

let multiTopics = [
  CollisionDetails,
  commercial_motor_vehicles,
  contributing_factors,
  crash_location,
  crash_report_forms,
  crash_specific_information,
  diagrams,
  executive_summary,
  glossary,
  location_identification_tips,
  narrative,
  occupant_information,
  overview,
  property_damage_witness_information,
  reporting_fatal_crashes,
  reporting_motor_vehicle_crashes_with_trains,
  roadway_details,
  submitting_a_crash_report,
  unit_operator_details,
  vehicle_details,
  county_list,
  data,
  rules,
  contactus,
  learning_module,
  release_notes,
  codes,
  faq,
];

// Default home route
const routes = [
  {
    path: "/",
    name: "GDOT Crash Reporting Instructions",
    component: HomeView,
  },
  // old url path, PWA service worker has issues with project root != domain root
  { path: "/crashsafety", redirect: "/" },
  // search page
  {
    path: "/search",
    name: "Search",
    component: SearchResults,
  },
  // add wildcard route at the end
  {
    path: "/:pathMatch(.*)*",
    name: "Page Not Found",
    component: PageNotFoundView,
  },
];

// Create route for all hot topic pages
for (let page of hotTopics) {
  routes.push({
    path: `${page.path.toLowerCase()}`,
    name: page.name,
    component: HotTopicsView,
    props: {
      page: page,
    },
  });
} 
  
// Create route for all multitopic pagess
for (let page of multiTopics) {
  routes.push({
    path: `${page.path.toLowerCase()}`,
    name: page.name,
    component: MultiTopicView,
    props: {
      page: page,
    },
  });
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      // Poll for the element to exist before scrolling (async content loading)
      return new Promise((resolve) => {
        const maxAttempts = 20;
        let attempts = 0;
        const checkElement = () => {
          const element = document.querySelector(to.hash);
          if (element) {
            resolve({
              el: to.hash,
              behavior: 'smooth',
            });
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(checkElement, 100);
          } else {
            // Give up after max attempts, scroll to top
            resolve({ top: 0 });
          }
        };
        // Start checking after a brief delay for initial render
        setTimeout(checkElement, 100);
      });
    } else if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

router.beforeEach((to, from, next) => {
  document.title = to.name;
  next();
});

console.log(routes.map((r) => r.path));

export default router;
